export type EngravingTechnique = "laser" | "hotstamping";

export interface EngravingOption {
  id: string;
  label: string;
  price: number; // acréscimo por peça
  technique: EngravingTechnique;
  leadTimeDays: number;
}

export const LASER_LEAD_DAYS = 10; // 7 produção + 3 separação
export const HOTSTAMPING_LEAD_DAYS = 30;
export const COMBO_MIN_QTY = 500;
export const COMBO_FLANELA_PRICE = 0.9;
export const HOTSTAMPING_PRICE = 0.9;
export const PLASTICO_MAX_PRICE = 5;
export const ESTOJO_MID_MAX_PRICE = 15;

// Categorias de óculos (grau e sol). Ajuste aqui quando os óculos forem cadastrados.
export const OCULOS_CATEGORIES = ["oculos", "oculos-de-sol", "oculos-de-grau", "lentes-solares"];
const FLANELA_SUBCATS = ["Flanela", "Saquinho"];

export interface EngravingProductInput {
  category?: string | null;
  subcategory?: string | null;
  price: number;
}

function laser(id: string, label: string, price: number): EngravingOption {
  return { id, label, price, technique: "laser", leadTimeDays: LASER_LEAD_DAYS };
}

export function getEngravingOptions(p: EngravingProductInput): EngravingOption[] {
  const cat = (p.category || "").toLowerCase();
  if (OCULOS_CATEGORIES.includes(cat)) {
    return [
      laser("frente_hastes", "Frente + 2 hastes", 4),
      laser("lente", "1 lente", 1.5),
      laser("hastes", "2 hastes", 3),
    ];
  }
  if (p.subcategory && FLANELA_SUBCATS.includes(p.subcategory)) {
    return [laser("logo", "Logomarca", 1)];
  }
  if (p.subcategory === "Estojo") {
    if (p.price <= PLASTICO_MAX_PRICE) {
      return [
        {
          id: "hotstamping",
          label: "Hotstamping",
          price: HOTSTAMPING_PRICE,
          technique: "hotstamping",
          leadTimeDays: HOTSTAMPING_LEAD_DAYS,
        },
      ];
    }
    if (p.price <= ESTOJO_MID_MAX_PRICE) return [laser("logo", "Gravação a laser", 2)];
    return [laser("logo", "Gravação a laser", 6)];
  }
  return [];
}

export function leadTimeLabel(days: number): string {
  return `${days} dias úteis`;
}

export function comboFlanelaDisponivel(opt: EngravingOption | undefined, quantity: number): boolean {
  return !!opt && opt.technique === "hotstamping" && quantity >= COMBO_MIN_QTY;
}

export interface EngravingState {
  enabled: boolean;
  engravingPrice?: number;
  comboFlanela?: boolean;
}

// Acréscimo de gravação por unidade (gravação + flanela combo).
export function engravingExtraPerUnit(pl?: EngravingState): number {
  if (!pl?.enabled) return 0;
  return (pl.engravingPrice || 0) + (pl.comboFlanela ? COMBO_FLANELA_PRICE : 0);
}

export interface EngravingSummaryInput {
  option?: string | null;
  technique?: string | null;
  price?: number | null;
  leadTimeDays?: number | null;
  comboFlanela?: boolean | null;
}

// Resumo legível da gravação para exibir no orçamento (carrinho, admin, PDF, impressão).
// `ascii` remove acentos para o PDF (a fonte Helvetica do react-pdf evita acentos no projeto).
export function engravingSummary(e: EngravingSummaryInput, ascii = false): string | null {
  if (!e?.option) return null;
  const peca = ascii ? "peca" : "peça";
  const uteis = ascii ? "uteis" : "úteis";
  const parts: string[] = [e.option];
  if (e.technique) parts.push(e.technique === "hotstamping" ? "hotstamping" : "laser");
  if (e.price != null) parts.push(`+ R$ ${e.price.toFixed(2)}/${peca}`);
  if (e.comboFlanela) parts.push(`+ flanela combo (R$ 0,90/${peca})`);
  if (e.leadTimeDays) parts.push(`prazo ${e.leadTimeDays} dias ${uteis}`);
  return parts.join(" · ");
}
